<template>
  <div class="brief-page bg-gray-100 min-h-full px-6 py-5">
    <div class="brief-toolbar max-w-[1080px] mx-auto flex items-center justify-between mb-4">
      <button type="button" class="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors" @click="$emit('back')">
        <font-awesome-icon :icon="['fas', 'arrow-left']" class="text-xs" />
        Back to targets
      </button>
      <div class="flex items-center gap-2">
        <button type="button" class="btn-secondary-sm" @click="copyLink">{{ copied ? 'Link copied' : 'Copy link' }}</button>
        <button v-if="guide" type="button" class="btn-primary-sm" :disabled="exporting" @click="printBrief">{{ exporting ? 'Preparing PDF…' : 'Download PDF' }}</button>
      </div>
    </div>

    <div v-if="loading" class="text-sm text-gray-500 py-8 text-center">Loading brief…</div>
    <div v-else-if="!guide" class="max-w-[1080px] mx-auto bg-white rounded border border-gray-200 px-6 py-10 text-center text-sm text-gray-600">
      The conversation guide for this practice hasn't been written yet.
    </div>

    <article v-else class="brief-doc max-w-[1080px] mx-auto bg-white shadow-growth-md">
      <header class="brief-head px-12 pt-10 pb-8 bg-gray-900 text-white">
        <div class="flex items-start justify-between gap-6">
          <div class="min-w-0">
            <div class="text-xs font-semibold uppercase tracking-[0.14em] text-primary-400">Conversation guide</div>
            <h1 class="sensitive text-[28px] leading-tight font-semibold mt-1.5">{{ data.name }}</h1>
            <div class="sensitive text-sm text-gray-300 mt-1">{{ place }} · {{ fmtInt(data.providers) }} providers · {{ fmtInt(data.lives) }} Medicare patients</div>
          </div>
          <div class="text-right text-xs text-gray-400 leading-5 shrink-0">Written {{ writtenOn }}</div>
        </div>
        <p class="sensitive mt-6 text-lg leading-8 font-medium text-white max-w-[820px]" data-demo-blur>{{ guide.headline }}</p>
      </header>

      <div class="px-12 pb-12">
        <section v-for="(block, bi) in shownSections" :key="block.name" class="brief-section pt-8" :class="bi ? 'mt-8 border-t border-gray-200' : ''">
          <h2 class="flex items-baseline gap-3 mb-4">
            <span class="text-xs font-bold tabular-nums text-primary-600">{{ String(bi + 1).padStart(2, '0') }}</span>
            <span class="text-base font-semibold text-gray-900">{{ block.title }}</span>
          </h2>

          <div v-if="block.name === 'story'" class="brief-story grid grid-cols-[minmax(0,1fr)_300px] gap-10 items-start">
            <p class="sensitive text-sm leading-7 text-gray-800" data-demo-blur>{{ guide.story }}</p>
            <dl class="bg-growth-25 rounded p-4 space-y-3 text-sm">
              <div><dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Size</dt><dd class="sensitive text-gray-800 mt-0.5" data-demo-blur>{{ guide.background.size }}</dd></div>
              <div><dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">ACO</dt><dd class="sensitive text-gray-800 mt-0.5" data-demo-blur>{{ guide.background.aco }}</dd></div>
              <div><dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Panel</dt><dd class="sensitive text-gray-800 mt-0.5" data-demo-blur>{{ guide.background.panel_trend }}</dd></div>
            </dl>
          </div>

          <div v-else-if="block.name === 'people'" class="space-y-3">
            <div v-for="(person, i) in guide.people" :key="i" class="brief-card flex gap-4 py-1">
              <span class="sensitive w-11 h-11 shrink-0 rounded-full bg-neutral-100 text-neutral-900 font-semibold text-sm flex items-center justify-center">{{ initials(person.name) }}</span>
              <div class="min-w-0">
                <div class="sensitive text-sm font-semibold text-gray-900">{{ person.name }}</div>
                <div class="sensitive text-sm text-gray-600">{{ person.role }}</div>
                <p class="sensitive text-sm text-gray-800 mt-2" data-demo-blur>{{ person.known }}</p>
                <p class="sensitive text-sm text-gray-800 mt-2" data-demo-blur><span class="font-semibold text-gray-900">Likely cares about:</span> {{ person.cares_about }}</p>
              </div>
            </div>
          </div>

          <div v-else-if="block.name === 'on_their_mind'" class="grid gap-4" :class="guide.on_their_mind.length > 2 ? 'grid-cols-3' : 'grid-cols-2'">
            <div v-for="(m, i) in guide.on_their_mind" :key="i" class="brief-card rounded bg-growth-25 p-4 border-t-4 border-primary-500">
              <div class="sensitive text-sm font-semibold text-gray-900 leading-6" data-demo-blur>{{ m.pressure }}</div>
              <p class="sensitive text-sm text-gray-700 mt-2" data-demo-blur>{{ m.why }}</p>
            </div>
          </div>

          <blockquote v-else-if="block.name === 'open_with'" class="brief-card rounded bg-primary-50 border-l-4 border-primary-600 px-6 py-5">
            <p class="sensitive text-base leading-8 text-gray-900" data-demo-blur>{{ guide.open_with }}</p>
          </blockquote>

          <div v-else-if="block.name === 'threads'" class="space-y-4">
            <div v-for="(t, i) in guide.threads" :key="i" class="brief-card rounded border border-gray-200 overflow-hidden">
              <div class="flex items-baseline gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
                <span class="text-sm font-bold text-gray-400 tabular-nums">{{ i + 1 }}</span>
                <span class="sensitive text-sm font-semibold text-gray-900" data-demo-blur>{{ t.topic }}</span>
              </div>
              <div class="px-5 py-4">
                <p class="sensitive text-sm text-gray-700" data-demo-blur>{{ t.why_it_matters }}</p>
                <div class="grid grid-cols-3 gap-5 mt-4">
                  <div>
                    <div class="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Ask</div>
                    <p class="sensitive text-sm leading-6 text-gray-900 font-medium" data-demo-blur>“{{ t.ask }}”</p>
                  </div>
                  <div>
                    <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Listen for</div>
                    <p class="sensitive text-sm text-gray-800" data-demo-blur>{{ t.listen_for }}</p>
                  </div>
                  <div>
                    <div class="text-xs font-semibold uppercase tracking-wide text-secondary-800 mb-1">Where we fit</div>
                    <p class="sensitive text-sm" :class="t.where_we_fit ? 'text-gray-800' : 'text-gray-400'" data-demo-blur>{{ t.where_we_fit || 'Just listen here.' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ul v-else-if="block.name === 'tread_carefully'" class="brief-card rounded bg-[#FDF6EC] border border-[#F1D9AE] divide-y divide-[#F1D9AE]">
            <li v-for="(c, i) in guide.tread_carefully" :key="i" class="flex gap-3 px-5 py-3">
              <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="mt-1 text-xs text-[#B7791F] shrink-0" />
              <div class="min-w-0">
                <div class="sensitive text-sm font-semibold text-gray-900" data-demo-blur>{{ c.topic }}</div>
                <p class="sensitive text-sm text-gray-700 mt-0.5" data-demo-blur>{{ c.why }}</p>
              </div>
            </li>
          </ul>

          <dl v-else-if="block.name === 'if_they_ask'" class="divide-y divide-gray-200 border-y border-gray-200">
            <div v-for="(q, i) in guide.if_they_ask" :key="i" class="brief-card grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-6 py-4">
              <dt class="sensitive text-sm font-semibold text-gray-900" data-demo-blur>“{{ q.question }}”</dt>
              <dd class="sensitive text-sm leading-6 text-gray-800" data-demo-blur>{{ q.answer }}</dd>
            </div>
          </dl>

          <div v-else-if="block.name === 'aco_history'">
            <p class="sensitive text-sm text-gray-800 mb-5 max-w-[820px]">{{ acoSummary }}</p>
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-gray-300 text-xs text-gray-500">
                  <th class="text-left font-semibold py-2 pr-4">Year</th>
                  <th class="text-left font-semibold py-2 px-4">ACO</th>
                  <th class="text-left font-semibold py-2 px-4">Program</th>
                  <th class="text-right font-semibold py-2 px-4">Their patients</th>
                  <th class="text-right font-semibold py-2 px-4">ACO savings</th>
                  <th class="text-right font-semibold py-2 px-4">ACO quality</th>
                  <th class="text-right font-semibold py-2 pl-4">ACO payout</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="y in acoTimeline" :key="y.year">
                  <td class="py-2 pr-4 tabular-nums text-gray-600">{{ y.year }}</td>
                  <td class="sensitive py-2 px-4 font-medium text-gray-900">{{ y.name }}</td>
                  <td class="py-2 px-4 text-gray-600">{{ [y.program, y.track].filter(Boolean).join(' · ') }}</td>
                  <td class="sensitive py-2 px-4 text-right tabular-nums text-gray-800">{{ y.lives == null ? '—' : fmtInt(y.lives) }}</td>
                  <td class="py-2 px-4 text-right tabular-nums" :class="y.savings == null ? 'text-gray-300' : y.savings < 0 ? 'text-red-dark' : 'text-gray-800'">{{ y.savings == null ? '—' : fmtPct(y.savings) }}</td>
                  <td class="py-2 px-4 text-right tabular-nums" :class="y.quality == null ? 'text-gray-300' : 'text-gray-800'">{{ y.quality == null ? '—' : fmtPct(y.quality) }}</td>
                  <td class="py-2 pl-4 text-right tabular-nums" :class="y.earned == null ? 'text-gray-300' : y.earned > 0 ? 'font-semibold text-green-dark' : 'text-gray-500'">{{ y.earned == null ? '—' : y.earned > 0 ? fmtMoney(y.earned) : 'None' }}</td>
                </tr>
              </tbody>
            </table>
            <div class="text-xs text-gray-500 mt-3">Savings, quality and payout are each ACO's own published CMS results for that year, across all its practices. A dash means CMS has not published that year yet.</div>
          </div>

          <div v-else-if="block.name === 'years'">
            <div v-if="years.length" class="text-xs text-gray-600">
                <svg :viewBox="`0 0 ${chartWidth} 200`" :style="{ width: chartWidth + 'px' }" class="max-w-full h-auto" role="img" aria-label="Medicare patients by year">
                  <text x="0" y="12" fill="#626264" font-size="12">Medicare patients</text>
                  <template v-for="(y, i) in years" :key="y.YEAR">
                    <rect
                      :x="10 + i * colWidth"
                      :y="180 - barHeight(y.LIVES)"
                      :width="colWidth - 22"
                      :height="barHeight(y.LIVES)"
                      :fill="i > breakIndex ? '#4C6E97' : '#C1D6F0'"
                      class="sensitive"
                    />
                    <text
                      :x="10 + i * colWidth + (colWidth - 22) / 2"
                      :y="180 - barHeight(y.LIVES) - 5"
                      text-anchor="middle"
                      fill="#353537"
                      class="sensitive"
                      font-size="12"
                    >{{ fmtInt(y.LIVES) }}</text>
                    <text :x="10 + i * colWidth + (colWidth - 22) / 2" y="196" text-anchor="middle" fill="#626264" font-size="12">{{ y.YEAR }}</text>
                  </template>
                  <template v-if="breakIndex >= 0">
                    <line :x1="breakX" y1="84" :x2="breakX" y2="184" stroke="#1E1F21" stroke-dasharray="3 3" />
                    <text :x="breakX + 4" y="82" fill="#1E1F21" font-weight="600" font-size="12">Panel changes</text>
                  </template>
                </svg>
                <svg v-if="pmpySeries.length > 1" :viewBox="`0 0 ${chartWidth} 96`" :style="{ width: chartWidth + 'px' }" class="max-w-full h-auto mt-6" role="img" aria-label="Cost per patient, adjusted">
                  <text x="0" y="12" fill="#626264" font-size="12">Cost per patient, adjusted for how sick they are</text>
                  <line x1="20" y1="60" :x2="chartWidth - 18" y2="60" stroke="#E8E9E9" />
                  <polyline :points="pmpyPoints" fill="none" stroke="#353537" stroke-width="1.5" />
                  <template v-for="p in pmpySeries" :key="p.year">
                    <circle :cx="10 + p.index * colWidth + (colWidth - 22) / 2" :cy="pmpyY(p.value)" r="2.5" fill="#353537" />
                    <text :x="10 + p.index * colWidth + (colWidth - 22) / 2" y="80" text-anchor="middle" fill="#353537" class="sensitive" font-size="12">${{ fmtInt(p.value) }}</text>
                  </template>
                </svg>
                <div v-if="countyRate" class="mt-1">County rate in {{ data.year }}: ${{ fmtInt(countyRate) }} per patient.</div>
              </div>
          </div>
        </section>
      </div>

      <footer class="brief-foot px-12 py-4 border-t border-gray-200 text-xs text-gray-400 flex justify-between">
        <span class="sensitive">Kairon Health · {{ data.name }}</span>
        <span>Growth Brief · template {{ data.template_id }}</span>
      </footer>
    </article>

    <details v-if="guide" class="brief-appendix max-w-[1080px] mx-auto mt-4 text-sm">
      <summary class="cursor-pointer text-gray-600 hover:text-gray-900 py-1">The Growth Graph data behind this guide</summary>
      <div v-for="(rows, label) in data.inputs" :key="label" class="mt-2 bg-white rounded border border-gray-200 overflow-hidden">
        <div class="px-3 py-2 text-xs font-semibold text-gray-900 bg-gray-50 border-b border-gray-200">{{ label }} <span class="font-normal text-gray-500">· {{ rows.length }} rows</span></div>
        <table v-if="rows.length" class="w-full text-xs">
          <tbody class="divide-y divide-gray-100">
            <tr v-for="(r, i) in rows" :key="i">
              <td class="py-1.5 px-3 text-gray-800 align-top font-mono break-all sensitive">{{ inputLine(r) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </details>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { exportBlockedInDemoMode } from '@/utils/demoSpoof'

const DEFAULT_SECTIONS = [
  { type: 'section', name: 'story', title: 'Their story' },
  { type: 'section', name: 'people', title: "Who you're likely talking to" },
  { type: 'section', name: 'on_their_mind', title: "What's probably on their mind" },
  { type: 'section', name: 'open_with', title: 'How to open' },
  { type: 'section', name: 'threads', title: 'Conversation threads' },
  { type: 'section', name: 'tread_carefully', title: 'Tread carefully' },
  { type: 'section', name: 'if_they_ask', title: 'If they ask' },
  { type: 'section', name: 'aco_history', title: 'Their ACO history' },
  { type: 'section', name: 'years', title: 'How the practice has changed' },
]



export default {
  name: 'BriefTab',
  components: { FontAwesomeIcon },
  props: {
    practiceKey: { type: String, default: null },
    tagLabels: { type: Object, default: () => ({}) },
  },
  emits: ['back'],
  data() {
    return {
      data: null, loading: false, copied: false, exporting: false }
  },
  computed: {
    guide() {
      const b = this.data && this.data.brief
      return b && b.story && b.threads ? b : null
    },
    shownSections() {
      const blocks = ((this.data && this.data.display_schema) || {}).blocks || DEFAULT_SECTIONS
      const g = this.guide
      return blocks.filter((b) => {
        if (b.type !== 'section') return false
        if (b.name === 'aco_history') return this.acoTimeline.length > 0
        if (b.name === 'years') return this.years.length > 0
        const v = g[b.name]
        return Array.isArray(v) ? v.length > 0 : !!v
      })
    },
    brief() {
      return this.data.brief
    },
    years() {
      return (this.data && this.data.years) || []
    },
    place() {
      return [this.data.county, this.data.state].filter(Boolean).join(', ')
    },
    writtenOn() {
      if (!this.data.updated_at) return ''
      return new Date(this.data.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },
    acoTimeline() {
      const byYear = {}
      for (const r of this.programRows) {
        if (!byYear[r.YEAR] || (r.LIVES || 0) > (byYear[r.YEAR].lives || 0)) {
          byYear[r.YEAR] = {
            year: r.YEAR, name: (r.PROGRAM_NAME || '').replace(/,?\s*LLC\.?$/i, ''), program: r.PROGRAM, track: r.TRACK, lives: r.LIVES,
            earned: r.EARNED_SAVINGS, savings: r.SAVINGS_RATE, quality: r.QUALITY_SCORE,
          }
        }
      }
      return Object.values(byYear).sort((x, y) => x.year - y.year)
    },
    acoSummary() {
      const t = this.acoTimeline
      if (!t.length) return ''
      const names = [...new Set(t.map((y) => y.name))]
      const last = t[t.length - 1]
      const paid = t.filter((y) => y.earned > 0).map((y) => y.year)
      const first = `In an ACO every year from ${t[0].year} to ${last.year}` + (names.length > 1 ? `, across ${names.length} different ACOs.` : `, all with ${names[0]}.`)
      const now = ` Most recently ${last.name}${last.track ? ` (${last.program}, ${last.track} track)` : ''}`
      const published = t.filter((y) => y.earned != null)
      const result = last.earned == null ? `; CMS has not published results for ${last.year} yet.` : last.earned > 0 ? `, which earned ${this.fmtMoney(last.earned)} in ${last.year}.` : `, which did not earn a payout in ${last.year}.`
      const history = !published.length ? '' : paid.length ? ` Their ACO earned a payout in ${paid.length} of the ${published.length} years with published results (${paid.join(', ')}).` : ` Their ACO earned no payout in any of the ${published.length} years with published results.`
      return first + now + result + history
    },
    colWidth() {
      return 66
    },
    chartWidth() {
      return 20 + this.years.length * 66
    },
    maxLives() {
      return Math.max(...this.years.map((y) => y.LIVES || 0), 1)
    },
    breakIndex() {
      const idx = this.years.findIndex((y) => y.PANEL_BREAK)
      return idx >= 0 ? idx - 1 : -1
    },
    breakX() {
      return 10 + (this.breakIndex + 1) * this.colWidth - 11
    },
    pmpySeries() {
      return this.years
        .map((y, index) => ({ year: y.YEAR, index, value: y.RISK_ADJUSTED_PMPY }))
        .filter((p) => p.value != null)
    },
    pmpyPoints() {
      return this.pmpySeries.map((p) => `${10 + p.index * this.colWidth + (this.colWidth - 22) / 2},${this.pmpyY(p.value)}`).join(' ')
    },
    programRows() {
      return [...((this.data && this.data.programs) || [])].sort((a, b) => b.YEAR - a.YEAR)
    },
    acoResultRows() {
      const rows = (this.data && this.data.aco_results) || []
      const current = this.programRows[0] ? this.programRows[0].PROGRAM_ID : null
      return rows.filter((r) => !current || r.PROGRAM_ID === current).sort((a, b) => b.YEAR - a.YEAR)
    },
    countyRate() {
      const last = this.years[this.years.length - 1]
      return last ? last.COUNTY_PMPY : null
    },
  },
  watch: {
    practiceKey() {
      this.load()
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      if (!this.practiceKey) return
      this.loading = true
      try {
        const { data } = await this.$axios.get(`/api/growth/briefs/${encodeURIComponent(this.practiceKey)}/`)
        this.data = data && data.practice_key ? data : null
      } finally {
        this.loading = false
      }
    },
    barHeight(v) {
      return Math.round(((v || 0) / this.maxLives) * 93)
    },
    pmpyY(v) {
      const vals = this.pmpySeries.map((p) => p.value)
      const min = Math.min(...vals)
      const max = Math.max(...vals)
      if (max === min) return 56
      return Math.round(56 - ((v - min) / (max - min)) * 40)
    },
    initials(name) {
      return String(name || '?').replace(/^(Dr\.?|Mr\.?|Ms\.?|Mrs\.?)\s+/i, '').split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase()
    },
    async printBrief() {
      if (exportBlockedInDemoMode() || this.exporting) return
      this.exporting = true
      try {
        const { data } = await this.$axios.get(`/api/growth/briefs/${encodeURIComponent(this.practiceKey)}/export/`, { params: { as: 'pdf' }, responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
        const link = document.createElement('a')
        link.href = url
        link.download = `Conversation guide - ${String(this.data.name || '').replace(/[\\/:*?"<>|]/g, ' ').replace(/[\s.]+$/, '')}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } finally {
        this.exporting = false
      }
    },
    inputLine(r) {
      return Object.entries(r).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k.toLowerCase()}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join('  ')
    },
    fmtInt(v) {
      return v == null ? '' : Math.round(v).toLocaleString()
    },
    fmtPct(v) {
      return v == null ? '' : (v * 100).toFixed(1) + '%'
    },
    fmtMoney(v) {
      if (v == null) return ''
      if (v <= -1000000) return '-$' + (-v / 1000000).toFixed(1) + 'M'
      if (v < 0) return '-$' + Math.round(-v / 1000) + 'K'
      if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M'
      if (v >= 1000) return '$' + Math.round(v / 1000) + 'K'
      return '$' + Math.round(v)
    },
    async copyLink() {
      const url = new URL(window.location.href)
      url.searchParams.set('practice', this.practiceKey)
      await navigator.clipboard.writeText(url.toString())
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },
  },
}
</script>

